import { Box, Checkbox, Grid, Typography } from "@mui/material";
import { useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import useStaticIPs from "../../../../api/hooks/useStaticIPs";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import BreezeStaticIPsModal from "../components/BreezeStaticIPsModal";
import GuidelineInstruction from "../components/GuidelineInstruction";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import MicrosoftAzureCloudGrantRolesSteps from "../MicrosoftAzureCloud/MicrosoftAzureCloudGrantRolesSteps";
import MicrosoftOffice365GenerateKeyStep from "../MicrosoftOffice365/MicrosoftOffice365GenerateKeyStep";
import microsoft_azure1 from "./images/microsoft_azure1.png";
import microsoft_azure11 from "./images/microsoft_azure11.png";
import microsoft_azure12 from "./images/microsoft_azure12.png";
import microsoft_azure2 from "./images/microsoft_azure2.png";
import microsoft_azure20 from "./images/microsoft_azure20.png";
import microsoft_azure3 from "./images/microsoft_azure3.png";
import microsoft_azure4 from "./images/microsoft_azure4.png";
import microsoft_azure5 from "./images/microsoft_azure5.png";
import microsoft_azure7 from "./images/microsoft_azure7.png";
import microsoft_azure8 from "./images/microsoft_azure8.png";
import microsoft_azure9 from "./images/microsoft_azure9.png";
import microsoft_tap_permissions from "./images/tap_permissions.png";
import MicrosoftAzureApplicationRoles from "./MicrosoftAzureApplicationRoles";
import MicrosoftAzureConnectForm from "./MicrosoftAzureConnectForm";

const MicrosoftAzureNewIntegration = ({
  integrationName,
  integrationDisplayName,
  permissions,
  windowsDefenderAtpPermissions,
  windows365OfficePermissions,
  onSubmit,
  onExpanded,
  handleExpandAll,
  handleCollapseAll,
  addToExpandedList,
  guideMode = false,
}) => {
  const colors = useColors();
  const [showStaticIPsModal, setShowStaticIPsModal] = useState(false);
  const { staticIPs: allowlistIps } = useStaticIPs();

  return (
    <div>
      {/* BUTTONS TO CONTROL EXPANSION */}
      <Box mb="20px">
        <Grid container spacing={2} direction={"row"}>
          {guideMode && <IntegrationGuideAlert />}

          <Grid item>
            <SecondaryButton onClick={handleExpandAll}>
              Expand All Screenshots
            </SecondaryButton>
          </Grid>
          <Grid item>
            <SecondaryButton onClick={handleCollapseAll}>
              Collapse All Screenshots
            </SecondaryButton>
          </Grid>
          <Grid item>
            <SecondaryButton onClick={() => setShowStaticIPsModal(true)}>
              View Breeze Static IPs
            </SecondaryButton>
          </Grid>
        </Grid>
      </Box>

      {/* SETUP INSTRUCTIONS */}
      <GuidelineStepByStep>
        <GuidelineStep title="Login to the Entra Portal">
          <Box style={{ display: "flex" }}>
            <PrimaryButton
              onClick={() => {
                window.open("https://entra.microsoft.com/", "_blank");
              }}
            >
              Sign In
            </PrimaryButton>
          </Box>
        </GuidelineStep>
        <GuidelineStep title="Create new application">
          <Typography variant="body1">Navigate to the</Typography>
          <Typography variant="body1">
            <strong>
              {" "}
              Applications {"->"} App registrations {"->"} New registration
            </strong>
          </Typography>
          <ScreenshotAccordion
            images={[
              {
                file: microsoft_azure1,
                altext: "Create new application",
              },
            ]}
            onExpanded={addToExpandedList}
          />
          <Typography variant="body1">
            Fill in the details and click Register.
          </Typography>
          <ScreenshotAccordion
            images={[
              {
                file: microsoft_azure2,
                altext: "Fill in the details and click Register",
              },
            ]}
            onExpanded={addToExpandedList}
          />
          <Typography variant="body1">
            Once the app is registered, note down the{" "}
            <strong> Application (client) ID </strong> and{" "}
            <strong> Directory (tenant) ID </strong>
          </Typography>
          <Typography variant="body1">
            copy and paste them under the corresponding fields in the{" "}
            <a href="#connectIntegration">form below</a> before continuing to
            the next step.
          </Typography>
          <ScreenshotAccordion
            images={[
              {
                file: microsoft_azure3,
                altext:
                  "Note down the Application (client) ID and Directory (tenant) ID",
              },
            ]}
            onExpanded={addToExpandedList}
          />
        </GuidelineStep>
        <GuidelineStep title="Assign API permissions">
          <Typography variant="body1">
            Navigate to the <strong>API permissions </strong> tab and click{" "}
            <strong> Add a permission </strong>
          </Typography>
          <ScreenshotAccordion
            images={[
              {
                file: microsoft_azure4,
                altext: "Navigate to the API permissions tab",
              },
            ]}
            onExpanded={addToExpandedList}
          />
          <Typography variant="body1">
            Select{" "}
            <strong> Microsoft Graph {"->"} Application permissions </strong>,
            and add <strong>{permissions.length} permissions:</strong>
          </Typography>
          <ScreenshotAccordion
            images={[
              {
                file: microsoft_azure5,
                altext: "Select Microsoft Graph -> Application permissions",
              },
            ]}
            onExpanded={addToExpandedList}
          />
          <Box
            sx={{
              width: "700px",
              backgroundColor: colors.grey_200,
              padding: "16px",
              borderRadius: "8px",
            }}
          >
            <Typography
              variant="subtitle2"
              color={colors.primary_darker}
              gutterBottom
            >
              The list of required permissions
            </Typography>
            <Typography variant="body2" gutterBottom>
              Check the box for each completed permission to track the progress
            </Typography>
            {permissions.map((permission, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <Checkbox />
                <Typography>{permission}</Typography>
              </Box>
            ))}
          </Box>
        </GuidelineStep>
        {integrationName === "microsoft_defender_endpoint" && (
          <GuidelineStep title="Configure WindowsDefenderATP">
            <Typography variant="body1">
              Select <strong>APIs my organization uses</strong> and search for
              <strong> WindowsDefenderATP </strong>
            </Typography>
            <ScreenshotAccordion
              images={[
                {
                  file: microsoft_azure11,
                  altext: "Grant WindowsDefenderATP permissions",
                },
              ]}
              onExpanded={addToExpandedList}
            />
            <Typography variant="body1">
              Then, add the following{" "}
              <strong>
                {windowsDefenderAtpPermissions?.length} permissions:
              </strong>
            </Typography>
            <ScreenshotAccordion
              images={[
                {
                  file: microsoft_tap_permissions,
                  altext: "Grant WindowsDefenderATP permissions",
                },
              ]}
              onExpanded={addToExpandedList}
            />
            <Box
              sx={{
                width: "700px",
                backgroundColor: colors.grey_200,
                padding: "16px",
                borderRadius: "8px",
              }}
            >
              <Typography
                variant="subtitle2"
                color={colors.primary_darker}
                gutterBottom
              >
                The list of required permissions
              </Typography>
              <Typography variant="body2" gutterBottom>
                Check the box for each completed permission to track the
                progress
              </Typography>
              {windowsDefenderAtpPermissions?.map((permission, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <Checkbox />
                  <Typography>{permission}</Typography>
                </Box>
              ))}
            </Box>
          </GuidelineStep>
        )}
        <GuidelineStep title="Assign API permissions">
          {integrationName === "microsoft_office_365" && (
            <>
              <Typography variant="body1">
                Select <strong>APIs my organization uses</strong> and search for
                <strong> Office 365 Exchange Online </strong>
              </Typography>
              <ScreenshotAccordion
                images={[
                  {
                    file: microsoft_azure12,
                    altext: "Grant Office 365 Exchange Online permissions",
                  },
                ]}
                onExpanded={addToExpandedList}
              />
              <Typography variant="body1">
                Add the following{" "}
                <strong>
                  {windows365OfficePermissions?.length} permissions:
                </strong>
              </Typography>
              <Box
                sx={{
                  width: "700px",
                  backgroundColor: colors.grey_200,
                  padding: "16px",
                  borderRadius: "8px",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color={colors.primary_darker}
                  gutterBottom
                >
                  The list of required permissions
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Check the box for each completed permission to track the
                  progress
                </Typography>
                {windows365OfficePermissions.map((permission, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <Checkbox />
                    <Typography>{permission}</Typography>
                  </Box>
                ))}
              </Box>
              <ScreenshotAccordion
                images={[
                  {
                    file: microsoft_azure20,
                    altext: "Grant Office 365 Exchange Online permissions",
                  },
                ]}
                onExpanded={addToExpandedList}
              />
            </>
          )}
          <Typography variant="body1">
            When done adding permissions, please review the complete list of
            permissions.
          </Typography>
          <Typography variant="body1">
            Then click on the <strong>"Grant admin consent for"</strong> button.
          </Typography>
          <Typography variant="body1">
            Press <strong>Yes</strong> to confirm.
          </Typography>
          <ScreenshotAccordion
            images={[
              {
                file: microsoft_azure7,
                altext: "Click on Grant admin consent",
              },
            ]}
            onExpanded={addToExpandedList}
          />
        </GuidelineStep>
        <GuidelineStep title="Create a client secret">
          <Typography variant="body1">
            Navigate to the <strong>Certificates & secrets </strong>tab and
            click <strong>New client secret</strong>
          </Typography>
          <Typography variant="body1">
            Fill in the description and change the expiry to{" "}
            <strong>24 months</strong> and click Add
          </Typography>
          <ScreenshotAccordion
            images={[
              {
                file: microsoft_azure8,
                altext: "Navigate to the Certificates & secrets tab",
              },
            ]}
            onExpanded={addToExpandedList}
          />
          <Typography variant="body1">
            Once the secret is created, copy the <strong>Value</strong> field of
            the secret and paste it in the{" "}
          </Typography>
          <Typography variant="body1">
            <a href="#connectIntegration">form below</a> under{" "}
            <strong>Client Secret Value</strong> before continuing to the next
            step.
          </Typography>
          <ScreenshotAccordion
            images={[
              {
                file: microsoft_azure9,
                altext: "Note down the value",
              },
            ]}
            onExpanded={addToExpandedList}
          />
        </GuidelineStep>
        {integrationName === "azure" && (
          <MicrosoftAzureCloudGrantRolesSteps
            addToExpandedList={addToExpandedList}
          />
        )}
        {integrationName === "microsoft_office_365" && (
          <MicrosoftOffice365GenerateKeyStep
            addToExpandedList={addToExpandedList}
          />
        )}
        {(integrationName === "microsoft_office_365" ||
          integrationName === "microsoft_entra_id") && (
          <MicrosoftAzureApplicationRoles
            addToExpandedList={addToExpandedList}
            integrationName={integrationName}
          />
        )}
        <GuidelineStep title="Add Breeze to the allowlist Network IP - Optional">
          <GuidelineInstruction
            text={
              <span>
                Add the following IP addresses to the allow list (e.g., CA
                policies, Azure KeyVault FW) to enable in-depth analysis:{" "}
                <strong>
                  {allowlistIps.map((ip, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{
                        margin: 0,
                        padding: "4px 0",
                      }}
                    >
                      <span>{ip}</span>
                      <CustomCopyButton
                        copyTitle={"IP Address"}
                        copyText={ip}
                      />
                    </Box>
                  ))}
                </strong>
              </span>
            }
          />
        </GuidelineStep>
        <GuidelineStep
          id="connectIntegration"
          title={"Connect " + integrationDisplayName}
        >
          <Box width={700}>
            <MicrosoftAzureConnectForm
              onSubmit={onSubmit}
              guideMode={guideMode}
            />
          </Box>
        </GuidelineStep>
      </GuidelineStepByStep>
      <BreezeStaticIPsModal
        open={showStaticIPsModal}
        onClose={() => setShowStaticIPsModal(false)}
      />
    </div>
  );
};

export default MicrosoftAzureNewIntegration;
